import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Plus, Minus, ArrowLeft, ArrowRight, Check, ShoppingCart, Calculator,
  Trash2, MapPin, ExternalLink, Package, X,
} from 'lucide-react';
import {
  FLOOR_PREP_PRODUCTS,
  EPOXY_PRIMERS,
  HIGH_BUILD_EPOXY,
  FLAKE_PRODUCTS,
  POLYASPARTIC_SEAL,
  HIGH_BUILD_SYSTEMS,
  FLAKE_COVERAGE,
  HIGH_BUILD_COLOUR_CHART,
} from '@/components/proepoxy/productData';
import { calculateOptimalPacks } from '@/components/proepoxy/utils';
import { submitProEpoxyOrder } from '@/components/proepoxy/submitOrder';
import PrioritySelector from '@/components/PrioritySelector';

export default function HighBuildGuidedStep1({ state, priority, setPriority, currentStep, goToStep, highBuildOrderMode, setHighBuildOrderMode, navigate, onExit }) {
  const {
    highBuildAreas, manualOrder,
    showInstallationGuide, setShowInstallationGuide,
    showColourChart, setShowColourChart,
    highBuildSteps,
    addHighBuildArea, removeHighBuildArea, updateHighBuildArea,
    initializeHighBuildProducts,
    calculateHighBuildTotal, getHighBuildItemCount,
    addPrimerItem, updatePrimerItem, removePrimerItem,
    addHighBuildItem, updateHighBuildItem, removeHighBuildItem,
    addFlakeItem, updateFlakeItem, removeFlakeItem,
    updateManualOrder, setManualOrder,
    calculateManualOrderTotal, getManualOrderItemCount,
    updateManualFloorPrepQty,
  } = state;
  return (
              <div className="space-y-6">
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <h2 className="text-xl font-bold text-[#27292E] mb-2">Define Your Areas</h2>
                  <p className="text-gray-600 mb-6">
                    Add the areas you&apos;ll be working on. Each area can have different products and specifications.
                  </p>

                  {highBuildAreas.length === 0 ? (
                    <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-xl">
                      <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500 mb-4">No areas added yet</p>
                      <Button
                        onClick={addHighBuildArea}
                        className="bg-[#7A0B7A] hover:bg-[#5a085a] text-white"
                        data-testid="hb-add-first-area-btn"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add First Area
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {highBuildAreas.map((area, index) => (
                        <div
                          key={area.id}
                          className="border-2 border-gray-200 rounded-xl p-4 hover:border-[#7A0B7A]/30 transition-colors"
                          data-testid={`hb-area-${index}`}
                        >
                          <div className="flex items-center gap-3 mb-4">
                            <div className="flex-1">
                              <Input
                                value={area.name}
                                onChange={(e) => updateHighBuildArea(area.id, 'name', e.target.value)}
                                className="text-lg font-bold border-0 p-0 h-auto focus:ring-0"
                                placeholder="Area name"
                                data-testid={`hb-area-name-${index}`}
                              />
                            </div>
                            <button
                              onClick={() => removeHighBuildArea(area.id)}
                              className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                              data-testid={`hb-remove-area-${index}`}
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                          
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Area Size (m²)</label>
                              <Input
                                type="number"
                                min="1"
                                value={area.m2}
                                onChange={(e) => updateHighBuildArea(area.id, 'm2', e.target.value)}
                                placeholder="e.g., 25"
                                className="w-full"
                                data-testid={`hb-area-m2-${index}`}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      <Button
                        onClick={addHighBuildArea}
                        variant="outline"
                        className="w-full border-2 border-dashed border-[#7A0B7A]/30 text-[#7A0B7A] hover:bg-[#7A0B7A]/5"
                        data-testid="hb-add-another-area-btn"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Another Area
                      </Button>
                    </div>
                  )}
                </div>

                {/* Navigation */}
                <div className="flex gap-4">
                  <Button
                    onClick={() => onExit()}
                    variant="outline"
                    className="flex-1 h-14 text-lg font-medium rounded-xl border-2 border-[#7A0B7A] text-[#7A0B7A] hover:bg-[#7A0B7A]/5"
                  >
                    <ArrowLeft className="w-5 h-5 mr-2" />
                    Back
                  </Button>
                  <Button
                    onClick={() => goToStep(2)}
                    disabled={highBuildAreas.length === 0 || highBuildAreas.some(a => !a.m2)}
                    className="flex-1 bg-[#7A0B7A] hover:bg-[#5a085a] text-white h-14 text-lg font-bold rounded-xl disabled:opacity-50"
                  >
                    Continue
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </div>
  );
}
